import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Heart, MessageCircle, Share2, Trophy, Home, Film, LogOut, Search, X } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { formatTempo, formatTimeAgo, getDiscordAvatarUrl, extractYouTubeId, getYouTubeThumbnail } from '../lib/timeUtils';
import CreateStoryModal from '../components/CreateStoryModal';
import { 
  fetchFeedPosts, 
  fetchAllRankings, 
  likePost, 
  searchUsers,
  fetchPostComments,
  fetchVideos,
  fetchAllMovieRankings,
  fetchUserProfile,
  fetchStories
} from '../lib/api';

// Componente de busca de usuários
const UserSearch = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    const performSearch = async () => {
      if (searchQuery.length < 2) {
        setSearchResults([]);
        setShowResults(false);
        return;
      }

      setSearching(true);
      try {
        const results = await searchUsers(searchQuery);
        setSearchResults(results || []);
        setShowResults(true);
      } catch (error) {
        console.error('Erro ao buscar usuários:', error);
        setSearchResults([]);
      } finally {
        setSearching(false);
      }
    };

    const timeoutId = setTimeout(performSearch, 300);
    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  const handleUserClick = (userId) => {
    navigate(`/perfil/${userId}`);
    setSearchQuery('');
    setShowResults(false);
  };

  return (
    <div className="relative flex items-center gap-2 bg-white/5 border border-purple-500/20 rounded-[25px] px-5 py-2.5 w-96">
      <Search size={16} className="text-gray-500" />
      <input 
        type="text" 
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        onFocus={() => searchQuery.length >= 2 && setShowResults(true)}
        onBlur={() => setTimeout(() => setShowResults(false), 200)}
                placeholder="Explore o Oasis..."
        className="bg-transparent outline-none text-sm w-full placeholder:text-gray-600 text-white"
      />
      {searchQuery && (
        <button
          onClick={() => {
            setSearchQuery('');
            setShowResults(false);
          }}
          className="text-gray-500 hover:text-white transition-colors"
        >
          <X size={16} />
        </button>
      )}
      
      {showResults && (searchResults.length > 0 || searching) && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-[#111827]/95 backdrop-blur-md border border-purple-500/20 rounded-[10px] max-h-80 overflow-y-auto z-50">
          {searching ? (
            <div className="p-4 text-center text-gray-400 text-sm">Buscando...</div>
          ) : searchResults.length > 0 ? (
            searchResults.map((user) => (
              <button
                key={user.id}
                onClick={() => handleUserClick(user.id)}
                className="w-full p-3 hover:bg-white/10 flex items-center gap-3 transition-colors border-b border-white/5 last:border-0"
              >
                <img
                  src={getDiscordAvatarUrl(user.discord_id, user.discord_avatar)}
                  alt={user.discord_username}
                  className="w-10 h-10 rounded-full border border-purple-500/40"
                />
                <div className="flex-1 text-left">
                  <p className="font-semibold text-sm text-white">{user.discord_username}</p>
                </div>
              </button>
            ))
          ) : (
            <div className="p-4 text-center text-gray-400 text-sm">Nenhum usuário encontrado</div>
          )}
        </div>
      )}
    </div>
  );
};

// Componente de carrossel de vídeos
const VideoCarousel = ({ videos, videoType, onVideoClick }) => {
  const carouselRef = useRef(null);

  const scrollCarousel = (direction) => {
    if (carouselRef.current) {
      const scrollAmount = 340;
      carouselRef.current.scrollBy({
        left: direction * scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  if (!videos || videos.length === 0) {
    return (
      <div className="text-center py-8 text-gray-400">
        Nenhum vídeo ainda. Seja o primeiro a compartilhar!
      </div>
    );
  }

  return (
    <div className="relative">
      <button
        onClick={() => scrollCarousel(-1)}
        className="absolute left-[-15px] top-1/2 -translate-y-1/2 w-[45px] h-[45px] bg-purple-600/90 hover:bg-purple-600 border-none rounded-full text-white text-xl cursor-pointer transition-all z-10 flex items-center justify-center shadow-[0_4px_15px_rgba(139,92,246,0.4)] hover:scale-110"
      >
        ‹
      </button>
      <div 
        ref={carouselRef}
        className="flex gap-5 overflow-x-auto scroll-smooth scrollbar-none"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        {videos.map((video) => (
          <div
            key={video.id}
            onClick={() => onVideoClick(videoType, video.id)}
            className="bg-black/30 border border-purple-500/15 rounded-2xl overflow-hidden transition-all cursor-pointer min-w-[320px] flex-shrink-0 hover:border-purple-500/40 hover:-translate-y-[5px] hover:shadow-[0_10px_30px_rgba(139,92,246,0.3)]"
          >
            <div className="relative w-full h-[180px] bg-gradient-to-br from-purple-600/20 to-indigo-600/20 flex items-center justify-center overflow-hidden">
              <img 
                src={getYouTubeThumbnail(video.youtube_video_id)} 
                alt={video.title || 'Video'}
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.target.src = 'https://via.placeholder.com/320x180?text=Video';
                }}
              />
              <div className="absolute w-[50px] h-[50px] bg-purple-600/90 hover:bg-purple-600 rounded-full flex items-center justify-center text-xl transition-all hover:scale-120">
                ▶
              </div>
            </div>
            <div className="p-4">
              <div className="text-sm font-semibold mb-2 text-white line-clamp-2">
                {video.title || 'Sem título'}
              </div>
              <div className="flex items-center gap-3 text-xs text-gray-500 mb-3">
                <div className="flex items-center gap-1.5">
                  <img 
                    src={getDiscordAvatarUrl(video.author_discord_id, video.author_avatar)}
                    alt={video.author_username}
                    className="w-5 h-5 rounded-full border border-purple-600"
                  />
                  <span>{video.author_username}</span>
                </div>
                <span>•</span>
                <span>{formatTimeAgo(video.created_at)}</span>
              </div>
              <div className="flex gap-3 pt-2.5 border-t border-purple-500/10">
                <div className="flex items-center gap-1.5 text-xs text-gray-400">👁️ {video.likes_count || 0}</div>
                <div className="flex items-center gap-1.5 text-xs text-gray-400">{videoType === 'shame-wall' ? '😂' : '🔥'} {video.likes_count || 0}</div>
                <div className="flex items-center gap-1.5 text-xs text-gray-400">💬 {video.comments_count || 0}</div>
              </div>
            </div>
          </div>
        ))}
      </div>
      <button
        onClick={() => scrollCarousel(1)}
        className="absolute right-[-15px] top-1/2 -translate-y-1/2 w-[45px] h-[45px] bg-purple-600/90 hover:bg-purple-600 border-none rounded-full text-white text-xl cursor-pointer transition-all z-10 flex items-center justify-center shadow-[0_4px_15px_rgba(139,92,246,0.4)] hover:scale-110"
      >
        ›
      </button>
    </div>
  );
};

const Feed = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [activeRanking, setActiveRanking] = useState('weekly');
  const [likingPosts, setLikingPosts] = useState(new Set());
  const [posts, setPosts] = useState([]);
  const [shameWallVideos, setShameWallVideos] = useState([]);
  const [bestClipsVideos, setBestClipsVideos] = useState([]);
  const [rankings, setRankings] = useState({
    weekly: [],
    monthly: [],
    global: []
  });
  const [movieRankings, setMovieRankings] = useState({
    weekly: [],
    monthly: [],
    global: []
  });
  const [userStats, setUserStats] = useState({
    movies_watched_count: 0,
    movie_reviews_count: 0
  });
  const [loading, setLoading] = useState(true);
  const [videosLoading, setVideosLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stories, setStories] = useState([]);
  const [storiesLoading, setStoriesLoading] = useState(true);
  const [showCreateStoryModal, setShowCreateStoryModal] = useState(false);

  // Buscar dados do usuário com estatísticas
  useEffect(() => {
    const loadUserStats = async () => {
      try {
        const profile = await fetchUserProfile();
        setUserStats({
          movies_watched_count: profile.movies_watched_count || 0,
          movie_reviews_count: profile.movie_reviews_count || 0
        });
      } catch (err) {
        console.error('Erro ao carregar estatísticas do usuário:', err);
      }
    };
    if (user) {
      loadUserStats();
    }
  }, [user]);

  // Buscar vídeos
  useEffect(() => {
    const loadVideos = async () => {
      try {
        setVideosLoading(true);
        const [shameWall, bestClips] = await Promise.all([
          fetchVideos('shame-wall', 20, 0),
          fetchVideos('best-clips', 20, 0)
        ]);
        setShameWallVideos(shameWall || []);
        setBestClipsVideos(bestClips || []);
      } catch (err) {
        console.error('Erro ao carregar vídeos:', err);
        setShameWallVideos([]);
        setBestClipsVideos([]);
      } finally {
        setVideosLoading(false);
      }
    };

    loadVideos();
  }, []);

  // Buscar stories
  useEffect(() => {
    const loadStories = async () => {
      try {
        setStoriesLoading(true);
        const storiesData = await fetchStories();
        setStories(storiesData || []);
      } catch (err) {
        console.error('Erro ao carregar stories:', err);
        setStories([]);
      } finally {
        setStoriesLoading(false);
      }
    };

    loadStories();
  }, [showCreateStoryModal]); // Recarregar quando modal de criar story fechar

  // Buscar posts do feed
  useEffect(() => {
    const loadFeed = async () => {
      try {
        setLoading(true);
        const postsData = await fetchFeedPosts();
        const postsWithComments = postsData || [];
        
        const postsWithCommentCounts = await Promise.all(
          postsWithComments.map(async (post) => {
            if (post.comments_count !== undefined) {
              return post;
            }
            try {
              const comments = await fetchPostComments(post.id);
              return {
                ...post,
                comments_count: comments?.length || 0
              };
            } catch (err) {
              console.error(`Erro ao buscar comentários do post ${post.id}:`, err);
              return {
                ...post,
                comments_count: 0
              };
            }
          })
        );
        
        setPosts(postsWithCommentCounts);
      } catch (err) {
        console.error('Erro ao carregar feed:', err);
        setError('Erro ao carregar o feed');
        setPosts([]);
      } finally {
        setLoading(false);
      }
    };

    loadFeed();
  }, []);

  // Buscar rankings de tempo
  useEffect(() => {
    const loadRankings = async () => {
      try {
        const rankingsData = await fetchAllRankings();
        
        const mapRankingData = (rankingArray, currentUserDiscordId, scoreField) => {
          if (!rankingArray || !Array.isArray(rankingArray)) return [];
          
          return rankingArray.map((item, index) => {
            const discordId = item.discord_id || item.user_id?.toString();
            const isCurrentUser = discordId === currentUserDiscordId;
            
            const score = item[scoreField] || 0;
            
            const avatar = item.discord_avatar 
              ? getDiscordAvatarUrl(discordId, item.discord_avatar)
              : getDiscordAvatarUrl(discordId, null);
            
            return {
              rank: index + 1,
              username: item.discord_username || item.nickname || 'Usuário',
              avatar: avatar,
              score: score,
              trend: "same",
              isCurrentUser: isCurrentUser,
              discord_id: discordId
            };
          });
        };
        
        const mappedRankings = {
          weekly: mapRankingData(rankingsData?.weekly || [], user?.discord_id, 'weekly_seconds'),
          monthly: mapRankingData(rankingsData?.monthly || [], user?.discord_id, 'monthly_seconds'),
          global: mapRankingData(rankingsData?.global || [], user?.discord_id, 'total_seconds')
        };
        
        setRankings(mappedRankings);
      } catch (err) {
        console.error('Erro ao carregar rankings:', err);
        setRankings({
          weekly: [],
          monthly: [],
          global: []
        });
      }
    };

    if (user) {
      loadRankings();
    }
  }, [user]);

  // Buscar rankings de filmes
  useEffect(() => {
    const loadMovieRankings = async () => {
      try {
        const movieRankingsData = await fetchAllMovieRankings(10);
        setMovieRankings({
          weekly: movieRankingsData?.weekly || [],
          monthly: movieRankingsData?.monthly || [],
          global: movieRankingsData?.global || []
        });
      } catch (err) {
        console.error('Erro ao carregar rankings de filmes:', err);
        setMovieRankings({
          weekly: [],
          monthly: [],
          global: []
        });
      }
    };

    if (user) {
      loadMovieRankings();
    }
  }, [user]);

  const handleLike = async (postId) => {
    if (likingPosts.has(postId)) return;
    
    try {
      setLikingPosts(prev => new Set(prev).add(postId));
      
      setPosts(prevPosts => prevPosts.map(post => {
        if (post.id === postId) {
          const newIsLiked = !post.is_liked;
          return {
            ...post,
            is_liked: newIsLiked,
            likes: newIsLiked ? (post.likes || 0) + 1 : Math.max((post.likes || 0) - 1, 0)
          };
        }
        return post;
      }));
      
      const response = await likePost(postId);
      
      setPosts(prevPosts => prevPosts.map(post => {
        if (post.id === postId) {
          return {
            ...post,
            is_liked: response.is_liked,
            likes: response.likes
          };
        }
        return post;
      }));
    } catch (err) {
      console.error('Erro ao curtir post:', err);
      setPosts(prevPosts => prevPosts.map(post => {
        if (post.id === postId) {
          const revertedIsLiked = !post.is_liked;
          return {
            ...post,
            is_liked: revertedIsLiked,
            likes: revertedIsLiked ? (post.likes || 0) + 1 : Math.max((post.likes || 0) - 1, 0)
          };
        }
        return post;
      }));
    } finally {
      setLikingPosts(prev => {
        const newSet = new Set(prev);
        newSet.delete(postId);
        return newSet;
      });
    }
  };

  const handleVideoClick = (videoType, videoId) => {
    navigate(`/video/${videoType}/${videoId}`);
  };

  const getRankIcon = (rank) => {
    if (rank === 1) return <Trophy className="text-yellow-400" size={20} />;
    if (rank === 2) return <span className="text-gray-300 font-bold text-base">🥈</span>;
    if (rank === 3) return <span className="text-orange-400 font-bold text-base">🥉</span>;
    return <span className="text-gray-500 font-bold text-sm">#{rank}</span>;
  };

  const getUserAvatar = () => {
    if (user?.discord_avatar) {
      return getDiscordAvatarUrl(user.discord_id, user.discord_avatar);
    }
    return getDiscordAvatarUrl(user?.discord_id || '0', null);
  };

  return (
    <div className="min-h-screen bg-[#0a0a14] text-white relative">
      {/* Background gradient */}
      <div className="fixed inset-0 pointer-events-none z-0" style={{
        background: `
          radial-gradient(circle at 20% 50%, rgba(139, 92, 246, 0.1) 0%, transparent 50%),
          radial-gradient(circle at 80% 80%, rgba(99, 102, 241, 0.1) 0%, transparent 50%)
        `
      }}></div>

      {/* Header */}
      <header className="fixed top-0 left-0 right-0 bg-[#0a0a14]/95 backdrop-blur-xl border-b border-purple-500/20 px-8 py-4 flex justify-between items-center z-[1000]">
        <Link to="/feed" className="flex items-center gap-3">
          <img src="/oasis-logo.ico" alt="Oasis" className="w-10 h-10 rounded-[10px]" />
          <span className="text-2xl font-bold">OASIS</span>
        </Link>
        
        <nav className="hidden md:flex gap-6 items-center">
          <Link to="/feed" className="text-gray-400 hover:text-purple-600 text-sm transition-all flex items-center gap-2 px-4 py-2 rounded-[10px] hover:bg-purple-600/10 font-medium">
            <Home size={18} />
            <span>Feed</span>
          </Link>
          <Link to="/feed" className="text-gray-400 hover:text-purple-600 text-sm transition-all flex items-center gap-2 px-4 py-2 rounded-[10px] hover:bg-purple-600/10 font-medium">
            <Film size={18} />
            <span>Filmes</span>
          </Link>
          <Link to="/ranking" className="text-gray-400 hover:text-purple-600 text-sm transition-all flex items-center gap-2 px-4 py-2 rounded-[10px] hover:bg-purple-600/10 font-medium">
            <Trophy size={18} />
            <span>Ranking</span>
          </Link>
        </nav>

        <div className="hidden lg:block">
          <UserSearch />
        </div>

        <div className="flex items-center gap-4">
          <Link to="/perfil" className="flex items-center gap-3 bg-purple-600/15 px-4 py-2 rounded-[30px] border border-purple-600/30 cursor-pointer transition-all hover:bg-purple-600/25 hover:-translate-y-[2px]">
            <span className="text-xs font-medium">{user?.discord_username || 'Usuário'}</span>
            <div className="w-[34px] h-[34px] bg-gradient-to-br from-purple-600 to-indigo-600 rounded-full flex items-center justify-center text-lg">
              {user?.discord_avatar ? (
                <img src={getUserAvatar()} alt="avatar" className="w-full h-full rounded-full" />
              ) : (
                '👤'
              )}
            </div>
          </Link>
          
          <button 
            onClick={logout}
            className="hidden md:flex items-center gap-2 px-3 py-1.5 bg-white/10 hover:bg-white/20 rounded-full transition-all"
          >
            <LogOut size={16} />
            <span className="text-sm">Sair</span>
          </button>
        </div>
      </header>

      {/* Main Container */}
      <div className="max-w-[1600px] mx-auto px-5 pt-20 grid grid-cols-1 lg:grid-cols-12 gap-6 relative z-10">
        
        {/* Left Sidebar */}
        <aside className="hidden lg:block lg:col-span-3 sticky top-[100px] h-fit">
          <div className="bg-[#111827]/60 border border-purple-500/15 rounded-2xl p-5 backdrop-blur-md mb-5">
            <div className="flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-all mb-2 text-gray-400 hover:bg-purple-600/15 hover:text-white hover:translate-x-[5px] active">
              <span className="text-xl w-6 text-center">🏠</span>
              <span className="text-sm font-medium">Início</span>
            </div>
            <Link to="/perfil" className="flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-all mb-2 text-gray-400 hover:bg-purple-600/15 hover:text-white hover:translate-x-[5px] no-underline">
              <span className="text-xl w-6 text-center">🎬</span>
              <span className="text-sm font-medium">Meus Filmes</span>
            </Link>
            <Link to="/shame-wall" className="flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-all mb-2 text-gray-400 hover:bg-purple-600/15 hover:text-white hover:translate-x-[5px] no-underline">
              <span className="text-xl w-6 text-center">😱</span>
              <span className="text-sm font-medium">Mural da Vergonha</span>
            </Link>
            <Link to="/best-clips" className="flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-all mb-2 text-gray-400 hover:bg-purple-600/15 hover:text-white hover:translate-x-[5px] no-underline">
              <span className="text-xl w-6 text-center">⭐</span>
              <span className="text-sm font-medium">Melhores Clipes</span>
            </Link>
          </div>

          <div className="bg-[#111827]/60 border border-purple-500/15 rounded-2xl p-5 backdrop-blur-md">
            <h3 className="text-sm mb-4 text-purple-600 flex items-center gap-2">
              📊 Suas Estatísticas
            </h3>
            <div className="flex justify-between py-2.5 border-b border-purple-500/10 text-xs last:border-0">
              <span className="text-gray-400">Filmes Assistidos</span>
              <span className="text-purple-600 font-semibold">{userStats.movies_watched_count}</span>
            </div>
            <div className="flex justify-between py-2.5 border-b border-purple-500/10 text-xs last:border-0">
              <span className="text-gray-400">Avaliações</span>
              <span className="text-purple-600 font-semibold">{userStats.movie_reviews_count}</span>
            </div>
          </div>
        </aside>

        {/* Main Content */}
        <main className="lg:col-span-6 space-y-6">
          
          {/* Stories Section */}
          <div className="bg-[#111827]/60 border border-purple-500/15 rounded-[20px] p-6 backdrop-blur-md relative overflow-hidden">
            <div className="absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-transparent via-purple-600 via-indigo-600 via-pink-600 to-transparent"></div>
            <div className="flex justify-between items-center mb-5">
              <h3 className="text-base flex items-center gap-2.5">✨ Stories do Oasis</h3>
            </div>
            {storiesLoading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto"></div>
              </div>
            ) : (
              <div className="flex gap-4.5 overflow-x-auto scroll-smooth scrollbar-none pb-2" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
                {/* Botão para adicionar story */}
                <div 
                  className="flex flex-col items-center gap-2.5 cursor-pointer transition-all min-w-[85px] flex-shrink-0"
                  onClick={() => setShowCreateStoryModal(true)}
                >
                  <div className="w-[75px] h-[75px] rounded-full bg-purple-600/20 border-2 border-dashed border-purple-600/50 flex items-center justify-center text-4xl font-bold text-purple-600 hover:bg-purple-600/30 hover:border-purple-600 transition-all">
                    +
                  </div>
                  <span className="text-xs text-gray-400 text-center max-w-[85px] truncate font-medium">Adicionar</span>
                </div>
                
                {/* Stories dos usuários */}
                {stories.map((story) => (
                  <div
                    key={story.id}
                    className="flex flex-col items-center gap-2.5 cursor-pointer transition-all min-w-[85px] flex-shrink-0 hover:-translate-y-2"
                    onClick={() => navigate(`/story/${story.id}`)}
                  >
                    <div className={`w-[75px] h-[75px] rounded-full p-[3px] relative ${
                      !story.is_viewed 
                        ? 'bg-gradient-to-r from-purple-600 via-indigo-600 to-pink-600' 
                        : 'bg-gray-600/50'
                    }`}>
                      <img
                        src={getDiscordAvatarUrl(story.author_discord_id, story.author_avatar)}
                        alt={story.author_username}
                        className="w-full h-full rounded-full border-[3px] border-[#0a0a14] object-cover"
                      />
                    </div>
                    <span className="text-xs text-gray-400 text-center max-w-[85px] truncate font-medium">{story.author_username}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Post Input */}
          <div className="bg-[#111827]/60 border border-purple-500/15 rounded-[20px] p-6 backdrop-blur-md transition-all hover:border-purple-500/30">
            <div className="flex gap-4 mb-4">
              <img
                src={getUserAvatar()}
                alt={user?.discord_username}
                className="w-12 h-12 rounded-full border-2 border-purple-600 flex-shrink-0 object-cover"
              />
              <input 
                type="text" 
                placeholder="O que você está assistindo no Oasis hoje?"
                onClick={() => navigate('/perfil')}
                className="w-full bg-white/5 border border-purple-500/20 rounded-xl px-5 py-4 text-sm text-white outline-none transition-all focus:border-purple-600 focus:bg-purple-600/5 focus:shadow-[0_0_20px_rgba(139,92,246,0.2)] placeholder:text-gray-600 cursor-pointer"
                readOnly
              />
            </div>
            <div className="flex justify-end items-center">
              <button 
                onClick={() => navigate('/perfil')}
                className="bg-gradient-to-br from-purple-600 to-indigo-600 border-none px-10 py-3 rounded-xl text-white font-semibold cursor-pointer transition-all text-sm shadow-[0_4px_15px_rgba(139,92,246,0.4)] hover:-translate-y-[2px] hover:shadow-[0_8px_25px_rgba(139,92,246,0.6)]"
              >
                PUBLICAR
              </button>
            </div>
          </div>

          {/* Mural da Vergonha Section */}
          <div className="bg-[#111827]/60 border border-purple-500/15 rounded-[20px] p-6 backdrop-blur-md animate-[slideIn_0.5s_ease-out]">
            <div className="flex justify-between items-center mb-5 pb-4 border-b-2 border-purple-500/20">
              <div className="flex items-center gap-3 text-lg font-semibold">
                <span className="text-2xl animate-[float_3s_ease-in-out_infinite]">😱</span>
                <span>Mural da Vergonha</span>
              </div>
              <button 
                onClick={() => {
                  const params = new URLSearchParams({ addVideo: 'shame-wall' });
                  navigate(`/perfil?${params.toString()}`);
                }}
                className="bg-purple-600/15 border border-purple-600/30 px-5 py-2 rounded-[10px] text-purple-600 cursor-pointer transition-all text-xs font-medium hover:bg-purple-600/25 hover:-translate-y-[2px]"
              >
                + Adicionar Vídeo
              </button>
            </div>
            {videosLoading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto"></div>
              </div>
            ) : (
              <VideoCarousel 
                videos={shameWallVideos} 
                videoType="shame-wall"
                onVideoClick={handleVideoClick}
              />
            )}
          </div>

          {/* Melhores Clipes Section */}
          <div className="bg-[#111827]/60 border border-purple-500/15 rounded-[20px] p-6 backdrop-blur-md animate-[slideIn_0.5s_ease-out]">
            <div className="flex justify-between items-center mb-5 pb-4 border-b-2 border-purple-500/20">
              <div className="flex items-center gap-3 text-lg font-semibold">
                <span className="text-2xl animate-[float_3s_ease-in-out_infinite]">⭐</span>
                <span>Melhores Clipes</span>
              </div>
              <button 
                onClick={() => {
                  const params = new URLSearchParams({ addVideo: 'best-clips' });
                  navigate(`/perfil?${params.toString()}`);
                }}
                className="bg-purple-600/15 border border-purple-600/30 px-5 py-2 rounded-[10px] text-purple-600 cursor-pointer transition-all text-xs font-medium hover:bg-purple-600/25 hover:-translate-y-[2px]"
              >
                + Adicionar Vídeo
              </button>
            </div>
            {videosLoading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto"></div>
              </div>
            ) : (
              <VideoCarousel 
                videos={bestClipsVideos} 
                videoType="best-clips"
                onVideoClick={handleVideoClick}
              />
            )}
          </div>

          {/* Posts Feed */}
          {loading ? (
            <div className="bg-[#111827]/60 border border-purple-500/15 rounded-[20px] p-8 text-center backdrop-blur-md">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
              <p className="text-gray-400">Carregando feed...</p>
            </div>
          ) : error ? (
            <div className="bg-[#111827]/60 border border-purple-500/15 rounded-[20px] p-8 text-center backdrop-blur-md">
              <p className="text-red-400">{error}</p>
            </div>
          ) : posts.length === 0 ? (
            <div className="bg-[#111827]/60 border border-purple-500/15 rounded-[20px] p-8 text-center backdrop-blur-md">
              <p className="text-gray-400">Nenhum post ainda. Seja o primeiro a compartilhar!</p>
            </div>
          ) : (
            posts.map((post) => {
              const postLiked = post.is_liked || false;
              const isLiking = likingPosts.has(post.id);
              const authorAvatar = post.author_avatar 
                ? getDiscordAvatarUrl(post.author_discord_id, post.author_avatar)
                : getDiscordAvatarUrl(post.author_discord_id || '0', null);
              
              return (
                <div 
                  key={post.id} 
                  className="bg-[#111827]/60 border border-purple-500/15 rounded-[20px] p-6 backdrop-blur-md transition-all animate-[slideIn_0.5s_ease-out] hover:border-purple-500/40 hover:shadow-[0_8px_30px_rgba(139,92,246,0.2)] cursor-pointer"
                  onClick={() => navigate(`/post/${post.id}`)}
                >
                  
                  {/* Post Header */}
                  <div className="flex items-center justify-between mb-5">
                    <div 
                      className="flex items-center gap-3"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/perfil/${post.author_id}`);
                      }}
                    >
                      <img 
                        src={authorAvatar} 
                        alt={post.author_username} 
                        className="w-12 h-12 rounded-full border-2 border-purple-600 cursor-pointer hover:border-purple-400 transition-colors shadow-[0_0_15px_rgba(139,92,246,0.4)]"
                        onError={(e) => {
                          e.target.src = getDiscordAvatarUrl(post.author_discord_id || '0', null);
                        }}
                      />
                      <div className="cursor-pointer">
                        <h3 className="text-[15px] text-white mb-1 font-semibold hover:text-purple-400 transition-colors">{post.author_username}</h3>
                        <span className="text-xs text-gray-500">{formatTimeAgo(post.created_at)}</span>
                      </div>
                    </div>
                    <div className="text-gray-500 cursor-pointer text-xl p-1 transition-all hover:text-purple-600 hover:scale-120">⋯</div>
                  </div>

                  {/* Movie Info */}
                  <div className="flex gap-5 my-5 bg-black/30 p-5 rounded-2xl border border-purple-500/10 transition-all hover:bg-black/50 hover:border-purple-500/30">
                    {post.movie_poster && (
                      <img 
                        src={post.movie_poster} 
                        alt={post.movie_title}
                        className="w-[120px] h-[180px] rounded-xl object-cover border-2 border-purple-600/30 shadow-[0_4px_20px_rgba(0,0,0,0.5)] transition-all hover:scale-105 hover:shadow-[0_8px_30px_rgba(139,92,246,0.4)]"
                      />
                    )}
                    <div className="flex-1">
                      <h4 className="text-lg mb-2.5 text-white font-semibold">{post.movie_title || 'Filme'}</h4>
                      {post.rating && (
                        <div className="flex items-center gap-2 mb-3">
                          <span className="text-yellow-400 text-base">
                            {'⭐'.repeat(Math.floor(post.rating))}
                          </span>
                          <span className="text-purple-600 font-semibold text-[15px]">{parseFloat(post.rating).toFixed(1)}</span>
                        </div>
                      )}
                      <p className="text-gray-400 text-xs leading-relaxed mb-3">{post.review}</p>
                      {post.movie_data?.genres && post.movie_data.genres.length > 0 && (
                        <span className="inline-block bg-purple-600/15 border border-purple-600/30 px-3.5 py-1.5 rounded-[20px] text-xs text-purple-400 font-medium">
                          {post.movie_data.genres[0]?.name || 'Filme'}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Post Actions */}
                  <div 
                    className="flex gap-5 mt-5 pt-5 border-t border-purple-500/10"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        handleLike(post.id);
                      }}
                      disabled={isLiking}
                      className="flex items-center gap-2 text-sm hover:text-red-400 transition-colors group disabled:opacity-50 disabled:cursor-not-allowed px-3 py-2 rounded-[10px] hover:bg-purple-600/10"
                    >
                      <Heart 
                        size={20} 
                        className={`${postLiked ? 'fill-red-500 text-red-500' : 'text-gray-400 group-hover:text-red-400'} transition-colors`}
                      />
                      <span className={postLiked ? 'text-red-500' : 'text-gray-400'}>
                        {post.likes || 0}
                      </span>
                    </button>
                    
                    <button className="flex items-center gap-2 text-sm text-gray-400 hover:text-purple-400 transition-colors px-3 py-2 rounded-[10px] hover:bg-purple-600/10">
                      <MessageCircle size={20} />
                      <span>{post.comments_count || 0}</span>
                    </button>
                    
                    <button className="flex items-center gap-2 text-sm text-gray-400 hover:text-pink-400 transition-colors px-3 py-2 rounded-[10px] hover:bg-purple-600/10">
                      <Share2 size={20} />
                      <span>Compartilhar</span>
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </main>

        {/* Right Sidebar */}
        <aside className="hidden lg:block lg:col-span-3 sticky top-[100px] h-fit space-y-6">
          
          {/* User Profile Card */}
          <div className="bg-[#111827]/60 border border-purple-500/15 rounded-[20px] p-6 backdrop-blur-md text-center">
            <div className="w-[90px] h-[90px] rounded-full mx-auto mb-4 border-[3px] border-purple-600 bg-gradient-to-br from-purple-600 to-indigo-600 flex items-center justify-center text-4xl shadow-[0_0_30px_rgba(139,92,246,0.5)] animate-[glow_3s_ease-in-out_infinite]">
              {user?.discord_avatar ? (
                <img src={getUserAvatar()} alt="avatar" className="w-full h-full rounded-full" />
              ) : (
                '👤'
              )}
            </div>
            <p className="text-xl font-semibold mb-1 text-white">{user?.discord_username || 'Usuário'}</p>
            <p className="text-gray-500 text-sm mb-5">{formatTempo(user?.total_seconds || 0)}</p>
            

            <div className="grid grid-cols-3 gap-4 mt-5">
              <div className="text-center">
                <span className="text-base font-semibold text-purple-600 block">{formatTempo(user?.total_seconds || 0)}</span>
                <span className="text-[11px] text-gray-500 mt-1 uppercase">Total</span>
              </div>
              <div className="text-center">
                <span className="text-base font-semibold text-purple-600 block">{formatTempo(user?.weekly_seconds || 0)}</span>
                <span className="text-[11px] text-gray-500 mt-1 uppercase">Semanal</span>
              </div>
              <div className="text-center">
                <span className="text-base font-semibold text-purple-600 block">{formatTempo(user?.monthly_seconds || 0)}</span>
                <span className="text-[11px] text-gray-500 mt-1 uppercase">Mensal</span>
              </div>
            </div>
          </div>

          {/* Ranking Card */}
          <div className="bg-[#111827]/60 border border-purple-500/15 rounded-[20px] p-6 backdrop-blur-md">
            <div className="flex items-center gap-2.5 mb-5">
              <Trophy className="text-yellow-400" size={20} />
              <h3 className="text-base font-semibold">Ranking</h3>
            </div>
            
            {/* Tabs */}
            <div className="flex gap-2 mb-5">
              {['weekly', 'monthly', 'global'].map((period) => (
                <button
                  key={period}
                  onClick={() => setActiveRanking(period)}
                  className={`flex-1 px-2.5 py-2.5 text-xs font-medium rounded-[10px] transition-all ${
                    activeRanking === period
                      ? 'bg-gradient-to-br from-purple-600 to-indigo-600 border-transparent text-white shadow-[0_4px_15px_rgba(139,92,246,0.4)]'
                      : 'bg-purple-600/5 border border-purple-600/15 text-gray-400 hover:text-white'
                  }`}
                >
                  {period === 'weekly' ? 'Semanal' : period === 'monthly' ? 'Mensal' : 'Global'}
                </button>
              ))}
            </div>

            {/* Ranking List */}
            <div className="space-y-2.5 max-h-96 overflow-y-auto">
              {rankings[activeRanking] && rankings[activeRanking].length > 0 ? (
                rankings[activeRanking].map((player, idx) => (
                  <div
                    key={player.discord_id || idx}
                    className={`flex items-center gap-3 p-3 rounded-xl transition-all ${
                      player.isCurrentUser
                        ? 'bg-gradient-to-r from-purple-600/20 to-fuchsia-500/20 border border-purple-500/50'
                        : 'bg-purple-600/3 border border-purple-500/10 hover:bg-purple-600/8 hover:border-purple-500/30 hover:translate-x-[5px]'
                    }`}
                  >
                    <div className="w-7 flex justify-center items-center">
                      {getRankIcon(player.rank)}
                    </div>
                    <img 
                      src={player.avatar} 
                      alt={player.username} 
                      className="w-[38px] h-[38px] rounded-full border-2 border-purple-500/40 object-cover"
                      onError={(e) => {
                        e.target.src = getDiscordAvatarUrl(player.discord_id || '0', null);
                      }}
                    />
                    <div className="flex-1 min-w-0">
                      <p className={`text-xs font-semibold truncate ${player.isCurrentUser ? 'text-purple-400' : 'text-white'}`}>
                        {player.username}
                      </p>
                      <p className="text-[11px] text-gray-500">{formatTempo(player.score)}</p>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-center text-gray-500 text-sm py-4">Carregando rankings...</p>
              )}
            </div>
          </div>
        </aside>
      </div>

      {/* Animations */}
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-10px); }
        }
        @keyframes glow {
          0%, 100% { box-shadow: 0 0 20px rgba(139, 92, 246, 0.3); }
          50% { box-shadow: 0 0 40px rgba(139, 92, 246, 0.6); }
        }
        @keyframes slideIn {
          from { opacity: 0; transform: translateX(-20px); }
          to { opacity: 1; transform: translateX(0); }
        }
        .scrollbar-none::-webkit-scrollbar {
          display: none;
        }
        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>

      {/* Modal para criar story */}
      <CreateStoryModal
        isOpen={showCreateStoryModal}
        onClose={() => setShowCreateStoryModal(false)}
        onStoryCreated={() => {
          // Recarregar stories será feito pelo useEffect que monitora showCreateStoryModal
        }}
      />
    </div>
  );
};

export default Feed;
